import { z } from 'zod'

export const registerSchema = z
  .object({
    email: z.string().email('Email inválido'),
    phone: z
      .string()
      .regex(/^\+595\d{8,9}$/, 'Teléfono debe tener formato +595XXXXXXXXX')
      .optional(),
    password: z
      .string()
      .min(8, 'Mínimo 8 caracteres')
      .regex(/[A-Z]/, 'Debe contener al menos una mayúscula')
      .regex(/[0-9]/, 'Debe contener al menos un número'),
    confirmPassword: z.string(),
    role: z.enum([
      'REMITENTE_ADMIN',
      'REMITENTE_OPERATOR',
      'TRANSPORTADORA_ADMIN',
      'TRANSPORTADORA_OPERATOR',
      'CONDUCTOR_AUTONOMO',
      'CONDUCTOR_VINCULADO',
    ]),
    nombre: z.string().min(2, 'Nombre muy corto').max(100),
    apellido: z.string().min(2).max(100),
    // Para roles de empresa
    empresaRuc: z
      .string()
      .regex(/^\d{6,8}-\d$/, 'RUC inválido. Formato: XXXXXXXX-X')
      .optional(),
    empresaRazonSocial: z.string().min(3).max(200).optional(),
    empresaDireccion: z.string().min(5).max(300).optional(),
    empresaCiudad: z.string().min(2).max(100).optional(),
    empresaTelefono: z.string().optional(),
    // Para conductores
    conductorCedula: z
      .string()
      .regex(/^\d{6,8}$/, 'Cédula debe tener entre 6 y 8 dígitos')
      .optional(),
    conductorLicenciaCategoria: z.string().optional(),
    vehiculoTipo: z.string().min(2).max(30).optional(),
    vehiculoPlaca: z
      .string()
      .regex(/^[A-Z]{3}\d{3,4}$/, 'Chapa inválida. Formato: ABC1234')
      .optional(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  })
  .superRefine((d, ctx) => {
    const isConductor = ['CONDUCTOR_AUTONOMO', 'CONDUCTOR_VINCULADO'].includes(d.role)
    if (isConductor) {
      if (!d.conductorCedula) {
        ctx.addIssue({ code: 'custom', message: 'Cédula de identidad requerida para conductores', path: ['conductorCedula'] })
      }
      if (!d.vehiculoTipo) {
        ctx.addIssue({ code: 'custom', message: 'Tipo de vehículo requerido', path: ['vehiculoTipo'] })
      }
      if (!d.vehiculoPlaca) {
        ctx.addIssue({ code: 'custom', message: 'Chapa del vehículo requerida', path: ['vehiculoPlaca'] })
      }
    }
  })

export const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(1, 'Contraseña requerida'),
  pushToken: z.string().optional(),
  platform: z.enum(['android', 'ios']).default('android').optional(),
  deviceId: z.string().max(128).optional(),
})

export const refreshTokenSchema = z.object({
  refreshToken: z.string().uuid('Token inválido'),
})

export const forgotPasswordSchema = z.object({
  email: z.string().email('Email inválido'),
})

export const resetPasswordSchema = z
  .object({
    token: z.string().min(1),
    password: z
      .string()
      .min(8)
      .regex(/[A-Z]/)
      .regex(/[0-9]/),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  })

export type RegisterInput = z.infer<typeof registerSchema>
export type LoginInput = z.infer<typeof loginSchema>
export type RefreshTokenInput = z.infer<typeof refreshTokenSchema>
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>
